import type { ExtendedUser } from '@/lib/oidcConfig';
import { canonicalizeRoles } from '@/lib/roles';

export type TenantUuidMap = Record<string, string>;

export const normalizeTenantSlug = (value?: string | null): string =>
  (value ?? '').trim().toLowerCase();

export const normalizeTenantUuidMap = (input: unknown): TenantUuidMap | undefined => {
  if (input == null) {
    return undefined;
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) {
      return undefined;
    }
    try {
      return normalizeTenantUuidMap(JSON.parse(trimmed));
    } catch {
      return undefined;
    }
  }

  if (Array.isArray(input)) {
    return input.reduce<TenantUuidMap | undefined>((acc, entry) => {
      const normalized = normalizeTenantUuidMap(entry);
      if (!normalized) {
        return acc;
      }
      return acc ? { ...acc, ...normalized } : normalized;
    }, undefined);
  }

  if (typeof input === 'object') {
    const map: TenantUuidMap = {};
    Object.entries(input as Record<string, unknown>).forEach(([slug, value]) => {
      if (typeof value !== 'string') {
        return;
      }
      const normalizedSlug = normalizeTenantSlug(slug);
      const normalizedUuid = value.trim();
      if (normalizedSlug && normalizedUuid) {
        map[normalizedSlug] = normalizedUuid;
      }
    });
    return Object.keys(map).length ? map : undefined;
  }

  return undefined;
};

export const resolveTenantUuidFromMap = (
  tenant: string | null | undefined,
  map?: TenantUuidMap,
): string | null => {
  if (!tenant || !map) {
    return null;
  }
  const normalized = normalizeTenantSlug(tenant);
  if (!normalized) {
    return null;
  }
  return map[normalized] ?? null;
};

const CROSS_TENANT_ROLES = new Set([
  'rugira_platform_admin',
  'rugira_support',
  'rugira_compliance_admin',
]);

const findCanonicalTenantSlug = (user?: Pick<ExtendedUser, 'tenant_roles'> | null): string | null => {
  if (!user?.tenant_roles) return null;
  for (const [slug, roles] of Object.entries(user.tenant_roles)) {
    const canonical = canonicalizeRoles(roles);
    if (canonical.some((role) => CROSS_TENANT_ROLES.has(role))) {
      return slug;
    }
  }
  return null;
};

const userHasCrossTenantRole = (
  user?: Pick<ExtendedUser, 'roles' | 'tenant_roles'> | null,
): boolean => {
  const fromMap = user?.tenant_roles
    ? Object.values(user.tenant_roles).some((roles) => {
        const canonical = canonicalizeRoles(roles);
        return canonical.some((role) => CROSS_TENANT_ROLES.has(role));
      })
    : false;
  if (fromMap) return true;
  const fromRoles = canonicalizeRoles(user?.roles ?? []);
  return fromRoles.some((role) => CROSS_TENANT_ROLES.has(role));
};

export const deriveTenantScope = (
  user?: Pick<
    ExtendedUser,
    | 'current_tenant'
    | 'tenant_id'
    | 'tenant_uuid'
    | 'current_tenant_uuid'
    | 'tenant_uuid_map'
    | 'tenant_roles'
    | 'roles'
  > | null,
  explicitTenant?: string | null,
): { slug: string; uuid: string | null; platformSlug: string; platformUuid: string | null } => {
  const defaultSlug =
    (import.meta.env.VITE_DEV_TENANT_ID as string | undefined) || 'default';
  const selectedSlug =
    explicitTenant ||
    user?.current_tenant ||
    user?.tenant_id ||
    defaultSlug;

  const selectedUuid =
    resolveTenantUuidFromMap(selectedSlug, user?.tenant_uuid_map) ||
    user?.current_tenant_uuid ||
    user?.tenant_uuid ||
    null;

  const canonicalTenant = findCanonicalTenantSlug(user);
  const prefersPlatformSlug =
    canonicalTenant || userHasCrossTenantRole(user) ? true : false;
  const platformSlug = prefersPlatformSlug
    ? canonicalTenant || defaultSlug
    : selectedSlug || defaultSlug;
  const platformUuid =
    resolveTenantUuidFromMap(platformSlug, user?.tenant_uuid_map) ||
    (platformSlug === selectedSlug
      ? selectedUuid
      : resolveTenantUuidFromMap(defaultSlug, user?.tenant_uuid_map)) ||
    user?.tenant_uuid ||
    null;

  return {
    slug: selectedSlug || defaultSlug,
    uuid: selectedUuid,
    platformSlug,
    platformUuid,
  };
};
